import { useMutation } from '@tanstack/react-query'
import { Alert, App, Button, Table } from 'antd'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import type { GroupDetailData } from './finance.types.ts'
import { snapshotObject } from './utils/finance-snapshot.ts'

export function GroupRevisionPreview({
  group: g,
  canManage,
  refresh
}: {
  group: GroupDetailData
  canManage: boolean
  refresh: () => Promise<unknown>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const id = g.id
  const draft = snapshotObject(g.revisionDraft)
  const publishRevision = useMutation(trpc.bill.publishGroupRevision.mutationOptions())
  const cancelRevision = useMutation(trpc.bill.cancelGroupRevision.mutationOptions())
  return (
    <>
      {canManage && (
        <Alert
          type="warning"
          title="修订草稿已保存"
          description="发布前请核对，新版不会覆盖原出单历史。"
          action={
            <div className="flex gap-2">
              <Button
                loading={cancelRevision.isPending}
                onClick={async () => {
                  try {
                    await cancelRevision.mutateAsync({ groupId: id, expectedUpdatedAt: g.updatedAt })
                    await refresh()
                  } catch (e) {
                    void message.error(e instanceof Error ? e.message : '取消失败')
                  }
                }}
              >
                取消修订
              </Button>
              <Button
                type="primary"
                loading={publishRevision.isPending}
                onClick={async () => {
                  try {
                    await publishRevision.mutateAsync({ groupId: id, expectedUpdatedAt: g.updatedAt })
                    await refresh()
                  } catch (e) {
                    void message.error(e instanceof Error ? e.message : '发布失败')
                  }
                }}
              >
                发布修订
              </Button>
            </div>
          }
        />
      )}
      {!!g.revisionDraft && (
        <div className="space-y-2 rounded-lg border border-amber-200 p-3">
          <p>
            修订原因：{String(draft.reason ?? '')} · 新版预计待收 ¥{formatAmount(draft.outstanding)}
          </p>
          <Table
            size="small"
            rowKey="billId"
            pagination={false}
            dataSource={
              Array.isArray(draft.items)
                ? (draft.items as { billId: number; billSnapshot: unknown; outstandingAmount: string }[])
                : []
            }
            columns={[
              { title: '账单 ID', dataIndex: 'billId' },
              { title: '费用', render: (_, row) => String(snapshotObject(row.billSnapshot).description ?? '') },
              { title: '新版待收', dataIndex: 'outstandingAmount', render: formatAmount }
            ]}
          />
        </div>
      )}
    </>
  )
}
